import { useAtomValue } from "jotai";
import { useState } from "react";
import { User } from "../../../../gql/__generated__/graphql";
import { GET_ALL_CREATORS } from "../../../apis/lambda/GraphQL/User/queries";
import { setupCompleteAtom, userInfoAtom } from "../../../atoms";
import CreatorCard from "../../../components/Cards/CreatorCard/CreatorCard";
import {
  FilterOption,
  SortOption,
} from "../../../components/Filter/FilterPanel";
import { calculateTotalFollowers } from "../../../utils/dataUtils";
import { DiscoverBaseComponent } from "../DiscoverBaseComponent";

export function DiscoverCreators() {
  const [searchTerm, setSearchTerm] = useState("");
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [minFollowers, setMinFollowers] = useState<number | "">("");
  const [monthlyGMV, setMonthlyGMV] = useState<number | "">("");
  const [minRating, setMinRating] = useState(0);
  const userInfo = useAtomValue(userInfoAtom);
  const setupComplete = useAtomValue(setupCompleteAtom);

  const filterOptions: FilterOption[] = [
    {
      id: "search",
      label: "Search",
      type: "search",
      placeholder: "Search creators by name or description",
      value: searchTerm,
      onChange: setSearchTerm,
      searchFields: ["name", "description"],
    },
    {
      id: "platforms",
      label: "Platforms",
      type: "multiSelect",
      placeholder: "Select platforms",
      data: [
        { value: "tiktok", label: "TikTok" },
        { value: "instagram", label: "Instagram" },
        { value: "youtube", label: "YouTube" },
        { value: "twitter", label: "Twitter" },
      ],
      value: platforms,
      onChange: setPlatforms,
      filterFn: (creator: User, selectedPlatforms: string[]) => {
        if (selectedPlatforms.length === 0) return true;
        if (!creator.socialMedia) return false;
        return selectedPlatforms.some(
          (platform) =>
            creator.socialMedia?.[platform as keyof typeof creator.socialMedia]
        );
      },
    },
    {
      id: "minFollowers",
      label: "Min Followers",
      type: "numberInput",
      placeholder: "e.g. 10,000",
      suffix: "+",
      value: minFollowers,
      onChange: setMinFollowers,
      filterFn: (creator: User, minValue: number | "") => {
        if (minValue === "") return true;
        const totalFollowers = calculateTotalFollowers(creator);
        return totalFollowers >= minValue;
      },
    },
    {
      id: "monthlyGMV",
      label: "Monthly GMV",
      type: "numberInput",
      placeholder: "e.g. 5,000",
      suffix: "$+",
      value: monthlyGMV,
      onChange: setMonthlyGMV,
      filterFn: (creator: User, minValue: number | "") => {
        if (minValue === "") return true;
        return (creator.monthlyGMV || 0) >= minValue;
      },
    },
    {
      id: "minRating",
      label: "Minimum Rating",
      type: "slider",
      min: 0,
      max: 5,
      step: 0.1,
      marks: [
        { value: 0, label: "0" },
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "4" },
        { value: 5, label: "5" },
      ],
      value: minRating,
      onChange: setMinRating,
      field: "rating",
    },
  ];

  const sortOptions: SortOption[] = [
    {
      value: "rating",
      label: "Highest Rating",
      sortFn: (a: User, b: User) => (b.rating ?? 0) - (a.rating ?? 0),
    },
    {
      value: "followers",
      label: "Most Followers",
      sortFn: (a: User, b: User) => {
        const aFollowers = calculateTotalFollowers(a);
        const bFollowers = calculateTotalFollowers(b);
        return bFollowers - aFollowers;
      },
    },
    {
      value: "gmv",
      label: "Highest Monthly GMV",
      sortFn: (a: User, b: User) => (b.monthlyGMV || 0) - (a.monthlyGMV || 0),
    },
    {
      value: "name",
      label: "Name (A-Z)",
      sortFn: (a: User, b: User) => (a.name ?? "").localeCompare(b.name ?? ""),
    },
  ];

  return (
    <DiscoverBaseComponent<User>
      title="Discover Creators"
      subtitle="Find the perfect creator to promote your brand"
      query={GET_ALL_CREATORS}
      dataKey="creators"
      filterOptions={filterOptions}
      sortOptions={sortOptions}
      renderCard={(creator) => (
        <CreatorCard
          creator={creator}
          products={[]}
          setupComplete={setupComplete}
        />
      )}
      initialSortBy="rating"
      queryVars={{ brandId: userInfo?.brandId || "" }}
      filterValidItems={(creator) => {
        // Only show creators with at least a name and image
        return !!creator.name && !!creator.description;
      }}
    />
  );
}
